import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Phone,
  FileText,
  Lock,
  Eye,
  Camera,
  MapPin,
  UserCheck,
  ChevronDown,
  ChevronUp,
  Truck,
  Clock,
  ShieldCheck,
  AlertOctagon,
  FileCheck,
  HelpCircle,
  ArrowRight,
  Sparkles,
  X,
  Menu,
  ExternalLink,
  ChevronRight,
  Info,
  CheckSquare,
  XCircle,
  FileSearch,
  PhoneCall,
  Calendar,
  DollarSign,
  Ban,
  Scale,
  RefreshCw
} from 'lucide-react';

export default function TermosSegurancaCarga() {
  const [expandedCard, setExpandedCard] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      // Intersection Observer for reveal animations
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

      document.querySelectorAll('[data-animate]').forEach(el => {
        observer.observe(el);
      });

      return () => observer.disconnect();
    };

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    
    // Initial check
    setTimeout(handleScroll, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const toggleCard = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  const cards = [
    {
      id: 'intro',
      title: "Antes de começar, uma conversa franca",
      icon: <Info className="w-6 h-6" />,
      color: "blue",
      type: "intro",
      content: "Este documento é o nosso acordo de confiança. Aqui a gente explica como funciona a proteção que oferecemos quando você fecha um frete pelo nosso chat. Leia com atenção — não é burocracia por burocracia, é para que todo mundo saiba exatamente onde está pisando.",
      alert: {
        type: "blue",
        text: "Ao usar a proteção, você aceita as condições aqui descritas. Simples assim."
      }
    },
    {
      id: 'o-que-e',
      title: "O que é a Proteção",
      icon: <Shield className="w-6 h-6" />,
      color: "purple",
      type: "text",
      content: "A BoraFrete oferece cobertura de até",
      highlight: "R$ 100.000,00",
      suffix: "para mercadorias transportadas em fretes negociados e confirmados exclusivamente pelo nosso chat.",
      alert: {
        type: "yellow",
        text: "A BoraFrete é plataforma de tecnologia. A gente conecta transportadoras e embarcadores, mas não entra na relação comercial entre vocês. A proteção é um benefício extra que oferecemos, não uma obrigação legal de transporte."
      }
    },
    {
      id: 'exclusoes',
      title: "O que não cobrimos",
      icon: <Ban className="w-6 h-6" />,
      color: "red",
      type: "exclusions",
      intro: "Tem carga que a proteção não alcança — seja por valor, risco ou característica do produto:",
      exclusions: [
        { category: "Metais", items: "Aço, inox, alumínio, cobre, ferro" },
        { category: "Agronegócio", items: "Adubo, fertilizantes, defensivos, café, algodão" },
        { category: "Tecnologia", items: "Celulares, computadores, notebooks, tablets" },
        { category: "Valores", items: "Dinheiro, ouro, jóias, relógios, pedras preciosas" },
        { category: "Saúde", items: "Medicamentos, vacinas (humano e veterinário)" },
        { category: "Outros", items: "Armas, combustíveis, pneus, cargas radioativas" }
      ],
      alert: {
        type: "blue",
        text: "Se sua carga não está na lista, pode estar coberta. Mas quando tiver dúvida, chama a gente antes de fechar."
      }
    },
    {
      id: 'cobertura',
      title: "Quando a proteção entra",
      icon: <ShieldCheck className="w-6 h-6" />,
      color: "emerald",
      type: "coverage",
      intro: "Só acionamos a proteção em três situações, sempre com prova documental:",
      coverages: [
        { 
          icon: <Lock className="w-6 h-6" />, 
          title: "Furto", 
          desc: "Subtração mediante escalada ou arrombamento",
          color: "emerald"
        },
        { 
          icon: <AlertTriangle className="w-6 h-6" />, 
          title: "Roubo", 
          desc: "Subtração com violência ou grave ameaça",
          color: "amber"
        },
        { 
          icon: <FileSearch className="w-6 h-6" />, 
          title: "Apropriação Indébita", 
          desc: "Desvio da carga pelo responsável",
          color: "blue"
        }
      ],
      alert: {
        type: "yellow",
        strong: "Prova necessária:",
        text: "Boletim de Ocorrência Policial ou documento oficial equivalente. Sem isso, não tem como seguir."
      }
    },
    {
      id: 'requisitos',
      title: "Quem pode usar",
      icon: <UserCheck className="w-6 h-6" />,
      color: "cyan",
      type: "table",
      intro: "Para ter direito à proteção, você precisa cumprir alguns requisitos:",
      rows: [
        { label: "Tempo de assinatura", value: "Mínimo 1 ano como transportadora na plataforma" },
        { label: "Motorista", value: "Perfil ativo, verificado e sem bloqueios" },
        { label: "PGR em dia", value: "Plano de Gerenciamento de Riscos cumprido integralmente" },
        { label: "Veículo na ANTT", value: "Inscrição ativa há pelo menos 3 meses" }
      ],
      note: "* Motorista ativo = cadastro verificado e sem restrições na plataforma."
    },
    {
      id: 'documentacao',
      title: "Documentação que pedimos",
      icon: <FileText className="w-6 h-6" />,
      color: "orange",
      type: "docs",
      intro: "Se acontecer algo, vamos precisar de papelada. Quanto mais organizado você estiver, mais rápido resolvemos:",
      docs: [
        {
          icon: <UserCheck className="w-5 h-5" />,
          title: "Motorista & Veículo",
          color: "blue",
          items: ["CNH do motorista", "CPF do ajudante (se tiver)", "CRLV do cavalo e carreta", "Vínculo ANTT"]
        },
        {
          icon: <FileCheck className="w-5 h-5" />,
          title: "Da Carga",
          color: "purple",
          items: ["NF-e e DANFe", "CT-e e DACT-e", "MDF-e e DAMDF-e", "CIOT e VPO"]
        },
        {
          icon: <AlertTriangle className="w-5 h-5" />,
          title: "Do Incidente",
          color: "red",
          items: ["Boletim de Ocorrência", "Oitiva/reconstituição policial"]
        }
      ],
      alert: {
        type: "gray",
        text: "Podemos pedir outros documentos se necessário. A ideia é entender o que aconteceu, não enrolar você."
      }
    },
    {
      id: 'acionar',
      title: "Como acionar a proteção",
      icon: <PhoneCall className="w-6 h-6" />,
      color: "blue",
      type: "contact",
      accent: true,
      phone: "(65) 3358-9139",
      breadcrumb: ["Empresa", "Cliente BoraFrete", "Proteção de Carga"],
      alerts: [
        { type: "yellow", text: "Regra de ouro: Quanto mais rápido você avisar, mais rápido a gente age." },
        { type: "red", strong: "Seguro próprio é obrigatório:", text: "No momento de acionar, você precisa enviar cópia da sua apólice de seguro de transporte vigente. Sem isso, não conseguimos analisar o caso." }
      ]
    },
    {
      id: 'pagamento',
      title: "Pagamento",
      icon: <DollarSign className="w-6 h-6" />,
      color: "emerald",
      type: "payment",
      content: "Depois que recebermos",
      highlight: "toda",
      suffix: "a documentação completa e correta, você recebe a proteção em até",
      days: "30 dias corridos",
      alert: {
        type: "gray",
        text: "Se faltar documento, se vier fora do prazo ou incompleto, o prazo para. A gente avisa o que está faltando, mas a responsabilidade de organizar é sua."
      }
    },
    {
      id: 'nao-aplica',
      title: "Quando a proteção não aplica",
      icon: <XCircle className="w-6 h-6" />,
      color: "red",
      type: "danger",
      danger: true,
      intro: "Não cobrimos — e somos claros sobre isso:",
      exclusions: [
        { num: "01", title: "Sem prova formal", desc: "Não apresentou BO ou documento oficial no prazo" },
        { num: "02", title: "Má-fé ou fraude", desc: "Atos dolosos da transportadora, motorista ou terceiros" },
        { num: "03", title: "Descumprimento do termo", desc: "Não seguiu o PGR ou políticas da BoraFrete" },
        { num: "04", title: "Carga proibida", desc: "Transportou o que está na lista ou produtos ilícitos" },
        { num: "05", title: "Desvio de rota", desc: "Caminho não autorizado, paradas não programadas" }
      ],
      warning: {
        title: "Nosso direito de proteger o sistema",
        text: "Se identificarmos sinistros recorrentes, indícios de fraude ou uso indevido da proteção, podemos remover a transportadora do programa imediatamente, sem indenização ou aviso prévio."
      }
    },
    {
      id: 'geral',
      title: "O que mais você precisa saber",
      icon: <Scale className="w-6 h-6" />,
      color: "slate",
      type: "info",
      items: [
        {
          icon: <RefreshCw className="w-5 h-5" />,
          title: "Alterações",
          text: "Podemos mudar condições, prazos e valores avisando antes. O que já foi contratado segue valendo até o fim do prazo acordado."
        },
        {
          icon: <Scale className="w-5 h-5" />,
          title: "Foro",
          text: "Qualquer divergência será resolvida em Cuiabá/MT, ou no foro de São Paulo/SP quando aplicável, conforme legislação."
        }
      ]
    },
    {
      id: 'resumo',
      title: "Resumindo",
      icon: <CheckSquare className="w-6 h-6" />,
      color: "brand",
      type: "final",
      final: true,
      content: "A proteção existe para te dar segurança quando tudo dá errado, mas ela tem regras.",
      highlight: "Cumpriu as regras, documentou direito e agiu com transparência?",
      suffix: "A gente está do seu lado.",
      warning: "Tentou burlar o sistema?",
      warningSuffix: "Aí não tem conversa."
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: "from-blue-500/20 to-cyan-500/20 border-blue-500/30 text-blue-400 hover:shadow-blue-500/20",
      purple: "from-purple-500/20 to-pink-500/20 border-purple-500/30 text-purple-400 hover:shadow-purple-500/20",
      emerald: "from-emerald-500/20 to-teal-500/20 border-emerald-500/30 text-emerald-400 hover:shadow-emerald-500/20",
      red: "from-red-500/20 to-rose-500/20 border-red-500/30 text-red-400 hover:shadow-red-500/20",
      cyan: "from-cyan-500/20 to-blue-500/20 border-cyan-500/30 text-cyan-400 hover:shadow-cyan-500/20",
      orange: "from-orange-500/20 to-amber-500/20 border-orange-500/30 text-orange-400 hover:shadow-orange-500/20",
      amber: "from-amber-500/20 to-orange-500/20 border-amber-500/30 text-amber-400 hover:shadow-amber-500/20",
      slate: "from-slate-500/20 to-gray-500/20 border-slate-500/30 text-slate-400 hover:shadow-slate-500/20",
      brand: "from-brand-500/20 to-purple-500/20 border-brand-500/30 text-brand-400 hover:shadow-brand-500/20"
    };
    return colors[color] || colors.blue;
  };

  const getAlertClasses = (type) => {
    const alerts = {
      blue: "bg-blue-500/10 border-blue-500/30 text-blue-300",
      yellow: "bg-amber-500/10 border-amber-500/30 text-amber-300",
      red: "bg-red-500/10 border-red-500/30 text-red-300",
      gray: "bg-slate-500/10 border-slate-500/30 text-slate-300"
    };
    return alerts[type] || alerts.blue;
  };

  return (
    <div className="min-h-screen bg-[#050508] text-slate-200 font-sans antialiased overflow-x-hidden selection:bg-brand-500/30 selection:text-brand-200">
      
      {/* Dynamic Background */}
      <div className="fixed inset-0 -z-10">
        {/* Animated gradient orbs following mouse */}
        <div 
          className="absolute w-[800px] h-[800px] rounded-full opacity-30 blur-[120px] transition-all duration-1000 ease-out"
          style={{
            background: 'radial-gradient(circle, rgba(59,130,246,0.4) 0%, transparent 70%)',
            top: '5%',
            right: '-20%',
            transform: `translate(${(mousePosition.x - window.innerWidth/2) * 0.02}px, ${(mousePosition.y - window.innerHeight/2) * 0.02}px)`
          }}
        />
        <div 
          className="absolute w-[600px] h-[600px] rounded-full opacity-25 blur-[100px] transition-all duration-1000 ease-out"
          style={{
            background: 'radial-gradient(circle, rgba(249,115,22,0.4) 0%, transparent 70%)',
            bottom: '10%',
            left: '-10%',
            transform: `translate(${(mousePosition.x - window.innerWidth/2) * -0.02}px, ${(mousePosition.y - window.innerHeight/2) * -0.02}px)`
          }}
        />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
        
        {/* Noise texture */}
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }} />
      </div>

      {/* Header */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrollY > 50 ? 'rgba(5, 5, 8, 0.85)' : 'transparent',
          backdropFilter: scrollY > 50 ? 'blur(20px) saturate(180%)' : 'none',
          borderBottom: scrollY > 50 ? '1px solid rgba(255,255,255,0.1)' : 'none'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-brand-500/50 transition-all duration-300 group-hover:scale-110">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div className="absolute inset-0 bg-brand-500 rounded-xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-bold text-white tracking-tight">
                  Bora<span className="text-brand-400">Frete</span>
                </span>
                <span className="block text-[10px] text-slate-500 uppercase tracking-widest">Proteção de Carga</span>
              </div>
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold backdrop-blur-sm">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span>Termo Ativo 2026</span>
              </div>
              
              {/* Mobile menu button */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              
              <Link 
                to="/"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-sm font-medium"
              >
                <span>Voltar ao app</span>
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-[#050508]/95 backdrop-blur-xl border-b border-white/10 p-4">
            <div className="flex flex-col gap-2">
              <Link to="/" className="flex items-center gap-2 px-4 py-3 rounded-lg bg-white/5 text-white">
                <Truck className="w-5 h-5" />
                <span>Voltar ao BoraFrete</span>
              </Link>
              <div className="flex items-center gap-2 px-4 py-3 text-emerald-400">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-sm">Termo Ativo 2026</span>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          
          {/* Floating badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur border border-white/10 mb-8 text-sm text-brand-400 shadow-2xl animate-[float_6s_ease-in-out_infinite]">
            <Shield className="w-4 h-4" />
            <span>Documento de Segurança</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-brand-400 bg-clip-text text-transparent">
              Termo de Segurança
            </span>
            <br />
            <span className="text-white">de Carga</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Nosso acordo de confiança para proteger sua mercadoria durante todo o transporte. 
            Proteção de até <span className="text-brand-400 font-semibold">R$ 100.000,00</span> com transparência total.
          </p>

          {/* Meta info */}
          <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              Vigente desde 24/03/2026
            </span>
            <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10">CNPJ: 65.852.223/0001-22</span>
            <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10">Cuiabá/MT</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-32 space-y-4">
        
        {cards.map((card, index) => (
          <article 
            key={card.id}
            id={card.id}
            data-animate
            className={`group relative overflow-hidden rounded-2xl border backdrop-blur-xl transition-all duration-500 hover:scale-[1.01] ${getColorClasses(card.color)} ${isVisible[card.id] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${card.danger ? 'bg-red-950/20' : card.final ? 'bg-gradient-to-br from-brand-500/10 to-purple-500/10' : 'bg-gradient-to-br'} ${card.accent ? 'ring-2 ring-brand-500/50' : ''}`}
            style={{ 
              transitionDelay: `${index * 100}ms`,
              background: card.danger ? 'linear-gradient(to bottom right, rgba(239, 68, 68, 0.1), rgba(0, 0, 0, 0.2))' : undefined
            }}
          >
            {/* Glow effect */}
            <div className={`absolute inset-0 bg-gradient-to-r ${getColorClasses(card.color).split(' ')[0]} opacity-0 group-hover:opacity-10 transition-opacity duration-700`} />
            
            {/* Accent border for special cards */}
            {card.accent && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-500 to-purple-500" />
            )}
            
            <button 
              onClick={() => toggleCard(card.id)}
              className="w-full p-6 sm:p-8 flex items-center justify-between text-left relative z-10"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getColorClasses(card.color)} bg-opacity-30 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {React.cloneElement(card.icon, { className: "w-6 h-6 text-white" })}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white group-hover:text-brand-200 transition-colors">
                    {card.title}
                  </h2>
                  {card.danger && (
                    <span className="inline-flex items-center gap-1 mt-1 text-xs text-red-400 font-medium">
                      <AlertTriangle className="w-3 h-3" />
                      Leia com atenção
                    </span>
                  )}
                </div>
              </div>
              <div className={`w-10 h-10 rounded-full bg-white/5 flex items-center justify-center transition-all duration-300 ${expandedCard === card.id ? 'rotate-180 bg-white/10' : 'group-hover:bg-white/10'}`}>
                <ChevronDown className="w-5 h-5 text-slate-400" />
              </div>
            </button>

            <div className={`overflow-hidden transition-all duration-700 ${expandedCard === card.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-6 pb-6 sm:px-8 sm:pb-8 pt-2 space-y-6">
                
                {/* Intro type */}
                {card.type === 'intro' && (
                  <>
                    <p className="text-slate-300 text-lg leading-relaxed">{card.content}</p>
                    {card.alert && (
                      <div className={`p-4 rounded-xl border ${getAlertClasses(card.alert.type)} flex items-start gap-3`}>
                        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span className="text-sm font-medium">{card.alert.text}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Text type with highlight */}
                {card.type === 'text' && (
                  <>
                    <p className="text-slate-300 text-lg leading-relaxed">
                      {card.content} <span className="text-white font-bold text-xl">{card.highlight}</span> {card.suffix}
                    </p>
                    {card.alert && (
                      <div className={`p-4 rounded-xl border ${getAlertClasses(card.alert.type)} flex items-start gap-3`}>
                        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span className="text-sm leading-relaxed">{card.alert.text}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Exclusions grid */}
                {card.type === 'exclusions' && (
                  <>
                    <p className="text-slate-300">{card.intro}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {card.exclusions.map((item, i) => (
                        <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-red-500/5 border border-red-500/20 hover:bg-red-500/10 transition-colors group/item">
                          <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" />
                          <div>
                            <span className="text-white font-semibold block text-sm">{item.category}</span>
                            <span className="text-slate-400 text-xs">{item.items}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {card.alert && (
                      <div className={`p-4 rounded-xl border ${getAlertClasses(card.alert.type)} flex items-start gap-3`}>
                        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{card.alert.text}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Coverage cards */}
                {card.type === 'coverage' && (
                  <>
                    <p className="text-slate-300">{card.intro}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {card.coverages.map((coverage, i) => (
                        <div key={i} className={`p-5 rounded-xl bg-gradient-to-br from-${coverage.color}-500/10 to-transparent border border-${coverage.color}-500/30 hover:border-${coverage.color}-500/50 transition-all group/coverage`}>
                          <div className={`w-12 h-12 rounded-xl bg-${coverage.color}-500/20 flex items-center justify-center mb-3 group-hover/coverage:scale-110 transition-transform`}>
                            {React.cloneElement(coverage.icon, { className: `w-6 h-6 text-${coverage.color}-400` })}
                          </div>
                          <h4 className="text-white font-semibold mb-1">{coverage.title}</h4>
                          <p className="text-slate-400 text-sm leading-relaxed">{coverage.desc}</p>
                        </div>
                      ))}
                    </div>
                    {card.alert && (
                      <div className={`p-4 rounded-xl border ${getAlertClasses(card.alert.type)} flex items-start gap-3`}>
                        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <strong className="text-white block mb-1">{card.alert.strong}</strong>
                          <span>{card.alert.text}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Table */}
                {card.type === 'table' && (
                  <>
                    <p className="text-slate-300">{card.intro}</p>
                    <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5">
                      <table className="w-full text-sm">
                        <tbody>
                          {card.rows.map((row, i) => (
                            <tr key={i} className="border-b border-white/10 last:border-0 hover:bg-white/5 transition-colors">
                              <td className="p-4 text-slate-300 font-medium w-1/3">{row.label}</td>
                              <td className="p-4 text-slate-400">{row.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {card.note && <p className="text-xs text-slate-500 italic">{card.note}</p>}
                  </>
                )}

                {/* Documents */}
                {card.type === 'docs' && (
                  <>
                    <p className="text-slate-300">{card.intro}</p>
                    <div className="grid grid-cols-1 gap-4">
                      {card.docs.map((doc, i) => (
                        <div key={i} className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all">
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`w-10 h-10 rounded-lg bg-${doc.color}-500/20 flex items-center justify-center`}>
                              {React.cloneElement(doc.icon, { className: `w-5 h-5 text-${doc.color}-400` })}
                            </div>
                            <h4 className="text-white font-semibold">{doc.title}</h4>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {doc.items.map((item, j) => (
                              <span key={j} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-${doc.color}-500/10 border border-${doc.color}-500/30 text-${doc.color}-300 text-xs`}>
                                <span className={`w-1.5 h-1.5 rounded-full bg-${doc.color}-400`} />
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {card.alert && (
                      <div className={`p-4 rounded-xl border ${getAlertClasses(card.alert.type)} text-sm`}>
                        {card.alert.text}
                      </div>
                    )}
                  </>
                )}

                {/* Contact accent card */}
                {card.type === 'contact' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-6 rounded-2xl bg-gradient-to-r from-brand-500/20 to-purple-500/20 border border-brand-500/30">
                      <div className="w-16 h-16 rounded-2xl bg-brand-500/20 flex items-center justify-center">
                        <PhoneCall className="w-8 h-8 text-brand-400" />
                      </div>
                      <div>
                        <span className="text-xs text-brand-400 uppercase tracking-wider font-semibold block mb-1">Canal Oficial</span>
                        <a href={`tel:${card.phone.replace(/\D/g, '')}`} className="text-3xl font-bold text-white hover:text-brand-400 transition-colors">
                          {card.phone}
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-slate-400 bg-white/5 p-3 rounded-lg">
                      {card.breadcrumb.map((crumb, i) => (
                        <React.Fragment key={i}>
                          <span className={i === card.breadcrumb.length - 1 ? 'text-brand-400 font-medium' : ''}>{crumb}</span>
                          {i < card.breadcrumb.length - 1 && <ChevronRight className="w-4 h-4" />}
                        </React.Fragment>
                      ))}
                    </div>
                    
                    {card.alerts?.map((alert, i) => (
                      <div key={i} className={`p-4 rounded-xl border ${getAlertClasses(alert.type)} text-sm`}>
                        {alert.strong && <strong className="text-white block mb-1">{alert.strong}</strong>}
                        {alert.text}
                      </div>
                    ))}
                  </div>
                )}

                {/* Payment */}
                {card.type === 'payment' && (
                  <>
                    <p className="text-slate-300 text-lg">
                      {card.content} <span className="text-white font-bold">{card.highlight}</span> {card.suffix} <span className="text-emerald-400 font-bold text-xl">{card.days}</span>.
                    </p>
                    {card.alert && (
                      <div className={`p-4 rounded-xl border ${getAlertClasses(card.alert.type)} text-sm`}>
                        {card.alert.text}
                      </div>
                    )}
                  </>
                )}

                {/* Danger exclusions */}
                {card.type === 'danger' && (
                  <>
                    <p className="text-slate-300">{card.intro}</p>
                    <div className="space-y-3">
                      {card.exclusions.map((item, i) => (
                        <div key={i} className="flex gap-4 p-4 rounded-xl bg-red-500/5 border border-red-500/20 hover:bg-red-500/10 transition-colors">
                          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center text-red-400 font-bold text-sm">
                            {item.num}
                          </div>
                          <div>
                            <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                            <p className="text-slate-400 text-sm">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {card.warning && (
                      <div className="p-5 rounded-xl bg-red-500/10 border border-red-500/30">
                        <div className="flex items-center gap-2 mb-2 text-red-400 font-semibold">
                          <AlertOctagon className="w-5 h-5" />
                          {card.warning.title}
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed">{card.warning.text}</p>
                      </div>
                    )}
                  </>
                )}

                {/* Info grid */}
                {card.type === 'info' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {card.items.map((item, i) => (
                      <div key={i} className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all group">
                        <div className="flex items-center gap-2 mb-3 text-brand-400 group-hover:scale-105 transition-transform">
                          {item.icon}
                          <h4 className="text-white font-semibold">{item.title}</h4>
                        </div>
                        <p className="text-slate-400 text-sm leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Final card */}
                {card.type === 'final' && (
                  <div className="text-center space-y-6">
                    <p className="text-slate-300 text-lg">{card.content}</p>
                    <div className="p-6 rounded-2xl bg-gradient-to-r from-brand-500/20 to-emerald-500/20 border border-brand-500/30">
                      <p className="text-white text-xl font-semibold mb-2">{card.highlight}</p>
                      <p className="text-slate-400">{card.suffix}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                      <p className="text-red-400 font-semibold mb-1">{card.warning}</p>
                      <p className="text-slate-400 text-sm">{card.warningSuffix}</p>
                    </div>
                    <div className="pt-6 border-t border-white/10">
                      <span className="text-2xl font-bold bg-gradient-to-r from-brand-400 to-purple-400 bg-clip-text text-transparent">BoraFrete</span>
                      <p className="text-slate-500 text-sm mt-2">Tecnologia que move sua carga com segurança.</p>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </article>
        ))}

        {/* CTA Section */}
        <div 
          data-animate
          id="cta"
          className={`mt-12 p-8 rounded-3xl bg-gradient-to-br from-brand-500/20 via-purple-500/20 to-blue-500/20 border border-brand-500/40 backdrop-blur-xl text-center relative overflow-hidden group transition-all duration-1000 ${isVisible['cta'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-brand-500/0 via-white/10 to-brand-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
          
          <h3 className="text-2xl font-bold text-white mb-4 relative z-10">Precisa de ajuda?</h3>
          <p className="text-slate-300 mb-8 max-w-lg mx-auto relative z-10">
            Nossa equipe está disponível para esclarecer qualquer dúvida sobre a proteção da sua carga.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 relative z-10">
            <a 
              href="https://wa.me/5565981181997?text=Olá! Tenho dúvidas sobre o Termo de Segurança da Carga"
              target="_blank"
              rel="noopener noreferrer"
              className="group/btn inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold rounded-xl transition-all shadow-lg shadow-green-500/25 hover:shadow-green-500/40 hover:scale-105"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Falar no WhatsApp
              <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
            </a>
            <Link 
              to="/termos-de-uso"
              className="inline-flex items-center gap-2 px-6 py-4 bg-white/5 hover:bg-white/10 text-slate-300 font-medium rounded-xl transition-all border border-white/10 hover:border-white/20"
            >
              <FileText className="w-4 h-4" />
              Ver Termos de Uso
            </Link>
          </div>
        </div>

      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-black/60 backdrop-blur-xl py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-white">Bora<span className="text-brand-400">Frete</span></span>
                <p className="text-xs text-slate-500">Tecnologia que move o Brasil 🇧🇷</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span>© 2026 BoraFrete</span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">CNPJ: 65.852.223/0001-22</span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">Cuiabá/MT</span>
            </div>
            
            <Link 
              to="/"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all text-sm border border-white/10"
            >
              <span>Termo de Segurança de Carga</span>
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            </Link>
          </div>
        </div>
      </footer>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #050508;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
      `}</style>
    </div>
  );
}